import { Component, ElementRef, HostListener, Input, OnInit, Output, EventEmitter} from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd, NavigationStart } from '@angular/router';
import { GenericConfig, RestUrlsConfig } from '@signature-it/ngx-generic/config';
import { ShareDataService, GoogleAnalyticsService, FeaturesService, LanguageService } from '@signature-it/ngx-generic/services';
import { ISearchSection, ILanguage } from '@signature-it/ngx-generic/interfaces';
import { UntypedFormControl } from '@angular/forms';
import { Observable, forkJoin, Subject } from 'rxjs';
import { SolrService } from '@signature-it/ngx-catalogue/services';
import { ProductFullSolrDTO } from '@signature-it/ngx-catalogue/models';
import { debounceTime, tap, switchMap, finalize, take } from 'rxjs/operators';
import { takeUntil} from "rxjs/operators";

declare var _: any;

@Component({
    selector: 'search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss'],
    standalone: false
})

export class SearchComponent implements  OnInit {

    static readonly COMMON_SEARCH_TYPES = ['common_search', 'custom'];
    key: string;
    @Input() type: string = GenericConfig.isDam ? 'media' : 'catalogue';
    searchSections: ISearchSection[] =  GenericConfig.searchSections ? GenericConfig.searchSections : null;
    searchSectionsCtrl = new UntypedFormControl();
    results: any;
    isLoading = false;
    isOpen: boolean = false;
    @Output() searchBtnClicked = new EventEmitter<boolean>();
    @Input() idInputToFous: string;
    @Input() clearKeyAfterSearch: boolean = false;
    @Input() searchPlaceholder: string = 'Header Search...';
    splitRow: boolean = GenericConfig.splitAutoCompleteSearchResults === false ? false : true;
    commonSearches: any;
    langCode: string;
    locationChanged = false;
    protected destroy$: Subject<void> = new Subject<void>();

    constructor(
        protected router: Router,
        protected route: ActivatedRoute,
        public solrService: SolrService,
        public shareSvc: ShareDataService,
        public el: ElementRef,
        protected analyticsSvc: GoogleAnalyticsService,
        protected featuresService: FeaturesService,
        public restUrlsConf: RestUrlsConfig,
        protected langSvc: LanguageService) {
        this.langSvc.getCurrentLanguage$().pipe(take(2)).subscribe((lang: ILanguage) => {
            this.langCode = lang.code;
        });
        this.router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                // to avoid opening the results section when the url is changed
                this.locationChanged = true;
            }
        });

    }

    search() {
        if(GenericConfig.gaSendSearchTerms)
            this.analyticsSvc.sendSearchTerm(this.key);
        this.router.navigate([`${this.type}/group/1`], { queryParams: {kw: this.key || '*'}});

        if (this.clearKeyAfterSearch) {
            this.key = '';
        }
        this.searchBtnClicked.emit(true);
        this.isOpen = false;
        window.scrollTo(0, 0);
    }

    setKey(val) {
        if (!val) return;
        this.key = val;
        this.search();
    }

    onFocus() {
        this.getCommonSearchData().subscribe(res => {
            if (res) {
                this.results = this.getGroups(res);
                if (this.results.length) {
                    this.isOpen = true;
                    this.setIsLast();
                }
            }
        })
    }

    onFocusOut(item) {
        if (item.isLast) {
            this.isOpen = false;
        }
    }

	ngOnInit() {
        if (!this.searchSections) return;
        this.searchSections = this.searchSections.filter(s => s.active);
        this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((queryParams) => {
            this.searchSectionsCtrl.setValue(queryParams.kw || '');
        });
        this.searchSectionsCtrl.valueChanges
            .pipe(
                debounceTime(500),
                tap(() => {
                    this.results = [];
                    this.isLoading = true;
                }),
                switchMap(value => this.getAutocompleteRes(value)
                    .pipe(
                        finalize(() => {
                            this.isLoading = false;
                        }),
                    )
                )
            )
            .subscribe(data => {
                this.results = data;
                this.isLoading = false;
                this.setIsLast();
                if (this.results.length && !this.locationChanged) {
                    this.isOpen = true;
                }
                this.locationChanged = false;
            });
    }

    private getAutocompleteRes(value) {
        const subscriptions = {};
        return new Observable((observer) => {
            if (!value) return observer.next([]);
            this.searchSections.forEach(section => {
                const isCSType = SearchComponent.COMMON_SEARCH_TYPES.indexOf(section.type) !== -1;

                if (section.type !== 'ShowAllResults' && !isCSType) {
                    subscriptions[section.type] = this.solrService.ngramRequest( value, section, 10, section.fq, true, false);
                }
                if (section.isFixed && isCSType) {
                    subscriptions[section.type] = this.getCommonSearchData(section.type);
                }
            });
            forkJoin(subscriptions).subscribe(data => {
                return observer.next(this.getGroups(data));
            });
        });
    }

    private setIsLast() {
        const resLength = this.results.length;
        if (resLength) {
            let item = this.results[resLength - 1].items;
            if (item) {
                const itemsLength = this.results[resLength - 1].items.length;
                if (itemsLength) {
                    this.results[resLength - 1].items[itemsLength - 1].isLast = true;
                }
            }
        }
    }

    private getCommonSearchData(type?) {
        return new Observable(observer => {
            if (this.commonSearches) {
                observer.next(this._getCSMappedData(type));
                observer.complete();
                return;
            }
            this.featuresService.getCommonSearches(this.langCode).subscribe(res => {
                this.commonSearches = res;
                 observer.next(this._getCSMappedData(type));
                 observer.complete();
            });
        })
    }

    private _getCSMappedData(type) {
        const csSections = this.searchSections.filter(s => SearchComponent.COMMON_SEARCH_TYPES.indexOf(s.type) > -1);
        const data = {};
        csSections.forEach(s => {
            if (!type || type && s.type === type) {
                data[s.type] = {
                    items: this.commonSearches.filter(cs => s.type === cs.type)
                };
            }
        });
       if (type) {
           return data[type];
       } return data;
    }

    private getGroups(data) {
        const groups = [];
        const orderField = this.shareSvc.isMobileMode() ? 'orderMobile' : 'order';
        Object.keys(data).forEach(type => {
            const section = this.searchSections.filter(s => s.type === type)[0];
            let value: any;
            value = data[type];
            let items = [];
            if (value['grouped']) {
                value['grouped'][value['responseHeader']['params']['group.field']]['groups'].map(g => {
                    items = items.concat(ProductFullSolrDTO.createFromArray(g.doclist.docs));
                });
            } else if (value['items']) { // for common search data
                items = value['items'];
            } else {
                items = ProductFullSolrDTO.createFromArray(value['response']['docs']);
            }

            section.order = section[orderField];
            if (items.length) {
                section['items'] = items;
                groups.push(section);
            }
        });
        if (groups.length) this.getShowAllSection(groups);
        return _.sortBy(groups, 'order');
    }

    getShowAllSection(groups) {
        let showAll = this.searchSections.filter(s => s.type === 'ShowAllResults');
        if (showAll.length) {
            groups.push(showAll[0]);
        }
        return;
    }

    optionSelectedFunc(type, item) {
        let route;
        let queryParams = {};
        switch (type) {
            case 'products':
                route = `product-page/${item['product_id_i']}`;
                break;
            case 'brands':
                route = `catalogue/group/1`;
                queryParams['BrandName_ss']  = item['BrandName_ss'][0];
                break;
            case 'categories':
                route = `catalogue/group/${item['group_id_i']}`;
                break;
            default: // for common search - multiple types
                route = item['redirect_url'].split('?')[0];
                queryParams = this.router.parseUrl(item['redirect_url']).queryParams;
                break;
        }
        this.isOpen = false;
        this.router.navigate([route], {queryParams});
        this.searchBtnClicked.emit(true);
    }

    displayFn(value) {
        if (!value) return '';
        return value.item[value.displayField];
    }

    @HostListener('document:click',  ['$event']) onClinkOutside(event: any) {
        if (!this.el.nativeElement.contains(event.target)) {
            this.isOpen = false;
        }
    }

}
